// ui/cpBudget.ts — control-point budget and sandbox mode.
//
// Extracted verbatim from an inline <script> in index.html. Still assigns
// window.CPBudget; client.ts reads it through cp() and treats a missing
// module as "no budget".

export {};

/* ---------------------------------------------------------------------------
 * CPBudget — control-point budget and sandbox mode.
 *
 * WHAT IT DOES
 *   Leaderboard runs are capped at LIMIT control points. Sandbox mode lifts
 *   the cap but makes the run ineligible to submit. This module owns the
 *   rule, the header readout, the header toggle and the submit gate.
 *
 * THE client.ts SIDE
 *   The anchors live in client.ts, so this module cannot count them. Four
 *   call sites over there complete the loop (all already wired):
 *
 *     updatePanel()          CPBudget.setCount(road.anchors.length - 2)
 *                            Every road mutation routes through here, so this
 *                            one call covers add / remove / reset / load.
 *     addSegment()           gate on CPBudget.canAdd()
 *     pointerdown            gate the click-to-insert path on canAdd()
 *     submitScore()          bail on !CPBudget.qualifies()
 *
 *   The count is INTERIOR anchors: road.anchors.length minus the two town
 *   endpoints, which are fixed, undraggable and not drawn as handles. Showing
 *   a number two higher than the dots on screen would just look wrong.
 *
 *   Client-side gating is UX, not enforcement. The server already receives
 *   the full `road` on submit, so it should derive the count itself from
 *   road.anchors.length - 2 and reject over-budget entries — never trust a
 *   count sent alongside.
 *
 * DELIBERATE CHOICES
 *   - Sandbox is NOT persisted. Someone who flips it on once and forgets
 *     would otherwise be silently unable to submit tomorrow, which is the
 *     single worst failure mode this feature has.
 *   - Turning sandbox off while over the limit is allowed. The chip goes red
 *     and submit stays blocked until they remove points — clearer than
 *     refusing the toggle and explaining why.
 *   - The chip and toggle stay hidden until setCount() lands, so deploying
 *     this file against an un-updated client.js degrades to "no feature"
 *     rather than a dead readout stuck on "—". A console warning fires.
 * ------------------------------------------------------------------------ */
(window as any).CPBudget = (function () {
  'use strict';

  var LIMIT = 15;          /* ← the one knob. Everything else derives from it. */

  var count   = null;      /* null until client.js reports; never guess. */
  var sandbox = false;

  var chip     = document.getElementById('cp-budget');
  var usedEl   = document.getElementById('cp-used');
  var limitEl  = document.getElementById('cp-limit');
  var warnEl   = document.getElementById('cp-warn');
  var sandBtn  = document.getElementById('btn-sandbox') as HTMLButtonElement | null;
  var sbName   = sandBtn && sandBtn.querySelector('.sb-name');
  var sbState  = sandBtn && sandBtn.querySelector('.sb-state');
  var submitEl = document.getElementById('btn-submit') as HTMLButtonElement | null;
  var instrEl  = document.getElementById('instr-10');

  /* True only while *we* are the reason submit is disabled, so a render
   * never re-enables a button client.js disabled for its own reasons
   * (mid-request, already submitted today, and so on). */
  var weDisabledSubmit = false;

  function t(key) { return window.I18N ? I18N.t(key) : key; }
  function fill(key, vals) {
    return t(key).replace(/\{(\w+)\}/g, function (m, k) {
      return vals[k] == null ? m : String(vals[k]);
    });
  }
  /* Matches the punctuation already used in btn_cars: French wants a space
   * before the colon, Chinese wants the fullwidth form. */
  function colon() {
    var l = window.I18N ? I18N.locale : 'en';
    return l === 'fr' ? ' : ' : l === 'zh' ? '\uff1a' : ': ';
  }

  function qualifies() { return count === null || (!sandbox && count <= LIMIT); }
  function canAdd()    { return sandbox || count === null || count < LIMIT; }

  /* Visible refusal. A "+ Segment" button that silently does nothing reads
   * as a bug; the flash points at the reason. */
  var flashTimer = null;
  function flashEl(el) {
    if (!el || el.hidden) return;
    el.classList.remove('flash');
    void el.offsetWidth;                /* restart the animation */
    el.classList.add('flash');
  }
  /* The chip shows the number that blocked the add; the toggle shows the
   * way out. Flashing both is what makes the glyph-only mobile button
   * discoverable — it introduces itself at the moment it becomes useful. */
  function refuse() {
    flashEl(chip);
    flashEl(sandBtn);
    /* animationend clears it normally, but under prefers-reduced-motion the
     * animation is `none` and that event never fires — so clear on a timer
     * too, or the class sticks around forever. */
    if (flashTimer) clearTimeout(flashTimer);
    flashTimer = setTimeout(function () {
      if (chip) chip.classList.remove('flash');
      if (sandBtn) sandBtn.classList.remove('flash');
    }, 1000);
  }
  [chip, sandBtn].forEach(function (el) {
    if (!el) return;
    el.addEventListener('animationend', function () { el.classList.remove('flash'); });
  });

  function render() {
    if (!chip) return;

    chip.hidden = (count === null);
    if (sandBtn) sandBtn.hidden = (count === null);
    if (count === null) return;

    if (usedEl)  usedEl.textContent  = String(count);
    if (limitEl) limitEl.textContent = sandbox ? '\u221e' : String(LIMIT);

    chip.classList.toggle('disqualified', sandbox || count > LIMIT);
    chip.classList.toggle('at-limit',     !sandbox && count === LIMIT);
    chip.title = sandbox ? t('cp_sandbox_hint')
                         : fill('instr_10', { n: LIMIT });

    if (sandBtn) {
      var name  = t('cp_sandbox') + colon();
      var state = t(sandbox ? 'on' : 'off');
      sandBtn.setAttribute('aria-pressed', sandbox ? 'true' : 'false');
      if (sbName)  sbName.textContent  = name;
      if (sbState) sbState.textContent = state;
      /* The mobile form is a bare switch and ∞ glyph, so the accessible name
       * and the tooltip have to carry the whole meaning. */
      sandBtn.setAttribute('aria-label', name + state);
      sandBtn.title = t('cp_sandbox_hint');
    }

    if (warnEl) {
      if (sandbox) {
        warnEl.textContent = t('cp_no_submit');
        warnEl.hidden = false;
      } else if (count > LIMIT) {
        warnEl.textContent = fill('cp_over_limit', { k: count - LIMIT, n: LIMIT });
        warnEl.hidden = false;
      } else {
        warnEl.hidden = true;
      }
    }

    if (submitEl) {
      if (!qualifies()) {
        submitEl.disabled = true;
        weDisabledSubmit = true;
      } else if (weDisabledSubmit) {
        submitEl.disabled = false;
        weDisabledSubmit = false;
      }
    }

    document.dispatchEvent(new CustomEvent('cpbudgetchange', {
      detail: { count: count, limit: LIMIT, sandbox: sandbox, qualifies: qualifies() }
    }));
  }

  /* instr_10 carries a {n} placeholder, which the generic I18N applier
   * writes out verbatim — substitute after every apply(). */
  function syncInstr() {
    if (instrEl) instrEl.textContent = fill('instr_10', { n: LIMIT });
  }

  function setCount(n) {
    n = Math.max(0, Math.round(Number(n) || 0));
    if (n === count) return;
    count = n;
    render();
  }

  function setSandbox(on) {
    on = !!on;
    if (on === sandbox) return;
    sandbox = on;
    render();
  }

  if (sandBtn) {
    sandBtn.addEventListener('click', function () { setSandbox(!sandbox); });
  }

  /* Capture-phase gate. Runs before client.js's own bubble-phase handlers
   * regardless of how or when it bound them, so "+ Segment" and "Submit"
   * are covered without touching client.js. Canvas clicks are not — see
   * hook 2 in the header comment. */
  document.addEventListener('click', function (ev) {
    // ev.target is EventTarget, which has no closest(). The runtime guard
    // below is the real check — this cast only lets the compiler see it.
    var el = ev.target as Element | null;
    if (!el || typeof el.closest !== 'function') return;

    if (el.closest('#btn-add') && !canAdd()) {
      ev.preventDefault();
      ev.stopImmediatePropagation();
      refuse();
      return;
    }
    if (el.closest('#btn-submit') && !qualifies()) {
      ev.preventDefault();
      ev.stopImmediatePropagation();
      refuse();
      if (warnEl && !warnEl.hidden) {
        warnEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, true);

  document.addEventListener('localechange', function () { syncInstr(); render(); });

  syncInstr();
  render();

  /* If client.js never calls setCount, the chip stays hidden and the rule
   * silently doesn't exist. Say so loudly in the console rather than
   * shipping a feature that looks fine and isn't there. */
  setTimeout(function () {
    if (count === null) {
      console.warn(
        '[CPBudget] No count reported — the control-point cap is inactive. ' +
        'client.ts must call CPBudget.setCount(road.anchors.length - 2) from ' +
        'updatePanel(), and gate adds on CPBudget.canAdd().'
      );
    }
  }, 3000);

  return {
    get LIMIT()   { return LIMIT; },
    get count()   { return count; },
    get sandbox() { return sandbox; },
    setCount: setCount,
    setSandbox: setSandbox,
    canAdd: canAdd,
    qualifies: qualifies,
    refuse: refuse
  };
})();
